import { useEffect, useId, useRef, useState, type ReactNode, type RefObject } from 'react'
import { motion, useReducedMotion, useScroll, useSpring } from 'framer-motion'
import { prefersReducedMotion } from './useReveal'

export type RoadmapStep = {
  title: string
  body: ReactNode
  meta?: string
}

type Cap = { x: number; y: number; vertical: boolean }

type Pipe = { d: string; caps: Cap[]; width: number; height: number }

function useStepTracking(listRef: RefObject<HTMLOListElement | null>) {
  useEffect(() => {
    const list = listRef.current
    if (!list || typeof window.IntersectionObserver !== 'function' || prefersReducedMotion()) return
    const items = Array.from(list.querySelectorAll<HTMLElement>(':scope > li'))
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          const item = entry.target as HTMLElement
          if (entry.isIntersecting) item.dataset.pathStep = 'reached'
          else if (entry.boundingClientRect.top > 0) item.dataset.pathStep = 'ahead'
        }
      },
      { rootMargin: '0px 0px -40% 0px' },
    )
    for (const item of items) {
      item.dataset.pathStep = 'ahead'
      observer.observe(item)
    }
    return () => {
      observer.disconnect()
      for (const item of items) delete item.dataset.pathStep
    }
  }, [listRef])
}

function connect(from: DOMRect, to: DOMRect, box: DOMRect) {
  const a = {
    left: from.left - box.left,
    right: from.right - box.left,
    top: from.top - box.top,
    bottom: from.bottom - box.top,
  }
  const b = {
    left: to.left - box.left,
    right: to.right - box.left,
    top: to.top - box.top,
    bottom: to.bottom - box.top,
  }
  const sameRow = Math.abs(a.top - b.top) < Math.min(from.height, to.height) / 2
  if (sameRow) {
    const forward = b.left >= a.right
    const start = { x: forward ? a.right : a.left, y: a.top + from.height * 0.62 }
    const end = { x: forward ? b.left : b.right, y: b.top + to.height * 0.62 }
    const reach = (end.x - start.x) / 2
    return {
      d: `M ${start.x} ${start.y} C ${start.x + reach} ${start.y} ${end.x - reach} ${end.y} ${end.x} ${end.y}`,
      caps: [
        { ...start, vertical: true },
        { ...end, vertical: true },
      ],
    }
  }
  const start = { x: a.left + from.width / 2, y: a.bottom }
  const end = { x: b.left + to.width / 2, y: b.top }
  const bend = Math.max(28, (end.y - start.y) * 0.6)
  return {
    d: `M ${start.x} ${start.y} C ${start.x} ${start.y + bend} ${end.x} ${end.y - bend} ${end.x} ${end.y}`,
    caps: [
      { ...start, vertical: false },
      { ...end, vertical: false },
    ],
  }
}

function usePipe(
  wrapperRef: RefObject<HTMLDivElement | null>,
  listRef: RefObject<HTMLOListElement | null>,
) {
  const [pipe, setPipe] = useState<Pipe | null>(null)

  useEffect(() => {
    const wrapper = wrapperRef.current
    const list = listRef.current
    if (!wrapper || !list) return
    const measure = () => {
      const box = wrapper.getBoundingClientRect()
      const cards = Array.from(list.querySelectorAll('[data-pipe-card]')).map((card) =>
        card.getBoundingClientRect(),
      )
      const segments: string[] = []
      const caps: Cap[] = []
      for (let index = 0; index < cards.length - 1; index += 1) {
        const link = connect(cards[index], cards[index + 1], box)
        segments.push(link.d)
        caps.push(...link.caps)
      }
      setPipe({ d: segments.join(' '), caps, width: box.width, height: box.height })
    }
    measure()
    if (typeof window.ResizeObserver !== 'function') return
    const observer = new ResizeObserver(measure)
    observer.observe(wrapper)
    return () => observer.disconnect()
  }, [wrapperRef, listRef])

  return pipe
}

export function RoadmapPath({ label, steps }: { label: string; steps: RoadmapStep[] }) {
  const wrapperRef = useRef<HTMLDivElement>(null)
  const listRef = useRef<HTMLOListElement>(null)
  const patternId = `pipe-${useId().replace(/[^a-zA-Z0-9_-]/g, '')}`
  const reduced = useReducedMotion()
  const { scrollYProgress } = useScroll({
    target: wrapperRef,
    offset: ['start 72%', 'end 62%'],
  })
  const fill = useSpring(scrollYProgress, { stiffness: 110, damping: 28, mass: 0.4 })
  const pipe = usePipe(wrapperRef, listRef)
  useStepTracking(listRef)

  return (
    <div ref={wrapperRef} className="relative mx-auto max-w-5xl">
      {pipe && pipe.d && (
        <svg
          aria-hidden="true"
          focusable="false"
          className="pointer-events-none absolute left-0 top-0 overflow-visible"
          width={pipe.width}
          height={pipe.height}
          viewBox={`0 0 ${pipe.width} ${pipe.height}`}
        >
          <defs>
            <pattern
              id={patternId}
              width="14"
              height="14"
              patternUnits="userSpaceOnUse"
              patternTransform="rotate(45)"
            >
              <rect width="7" height="14" fill="#00e5ff" />
              <rect x="7" width="7" height="14" fill="#0b9fbd" />
            </pattern>
          </defs>
          <path d={pipe.d} fill="none" stroke="#0f1215" strokeWidth={20} strokeLinecap="round" />
          <path d={pipe.d} fill="none" stroke="#1b2026" strokeWidth={13} strokeLinecap="round" />
          <motion.path
            d={pipe.d}
            fill="none"
            stroke={`url(#${patternId})`}
            strokeWidth={13}
            strokeLinecap="round"
            style={{ pathLength: reduced ? 1 : fill }}
            className="[filter:drop-shadow(0_0_10px_rgba(0,229,255,0.55))]"
          />
          {pipe.caps.map((cap) => (
            <rect
              key={`${cap.x}-${cap.y}`}
              x={cap.x - (cap.vertical ? 7 : 22)}
              y={cap.y - (cap.vertical ? 22 : 7)}
              width={cap.vertical ? 14 : 44}
              height={cap.vertical ? 44 : 14}
              rx={7}
              fill="#15191e"
              stroke="rgba(255,255,255,0.12)"
            />
          ))}
        </svg>
      )}
      <ol
        ref={listRef}
        aria-label={label}
        className="relative grid gap-16 md:grid-cols-2 md:gap-x-28 md:gap-y-24"
      >
        {steps.map((step, index) => (
          <li key={step.title} className="group relative">
            <div
              data-pipe-card
              className="spotlight-card relative h-full rounded-[22px] border border-[var(--surface-border)] bg-[linear-gradient(180deg,rgba(19,22,26,0.96),rgba(11,13,16,0.96))] p-6 shadow-[var(--shadow-lg)] transition-[border-color,box-shadow] duration-500 group-data-[path-step=reached]:border-[var(--color-brand-accent)]/35 group-data-[path-step=reached]:shadow-[0_30px_80px_-40px_rgba(0,229,255,0.55)] sm:p-7"
            >
              <div className="flex items-baseline justify-between gap-4">
                <span className="font-display text-5xl font-semibold leading-none tracking-[-0.04em] text-white/15 transition-colors duration-500 group-data-[path-step=reached]:text-[var(--color-brand-accent)]">
                  {String(index + 1).padStart(2, '0')}
                </span>
                <span className="font-code text-[11px] uppercase tracking-[0.22em] text-[var(--surface-muted)]">
                  Step {index + 1} / {steps.length}
                </span>
              </div>
              <h3 className="mt-5 font-display text-xl font-semibold tracking-[-0.01em] text-[var(--text-strong)] sm:text-[22px]">
                {step.title}
              </h3>
              <div className="mt-2 text-sm leading-relaxed text-[var(--surface-muted)] sm:text-[15px]">
                {step.body}
              </div>
              {step.meta && (
                <p className="mt-5 inline-flex items-center gap-2 rounded-full border border-[var(--color-brand-accent)]/25 bg-[var(--color-brand-accent)]/[0.07] px-3 py-1 font-code text-[11px] text-[var(--color-brand-accent-readable)]">
                  {step.meta}
                </p>
              )}
            </div>
          </li>
        ))}
      </ol>
    </div>
  )
}
